import { z, ZodRawShape } from 'zod';

import { assertNever } from '../../assertNever';
import { Form, FormElement } from './Form';

/**
 * A schema is a declaration of the shape of data we need to collect (to be
 * attached to either a workflow or a step, TBD).
 *
 * Schemas are related to form elements but if at all possible we should strive
 * to declare all business logic at the schema layer and keep the Form
 * enhancements to the Schema types strictly presentational (i.e. selects instead
 * of radios, classes, that kind of thing). Doing this will allow us to use the
 * Schema more easily outside of the UI layer.
 *
 * TODO
 * - Will we need ways to configure & specify complex validations in future? i.e.
 *   dependencies between fields? Most use cases I've seen around this involve
 *   geography (i.e. if Country === USA then State is required, probably things we
 *   could special-case here)
 * - Conditional fields? Let's avoid if at all possible, but if we need them then
 *   this gets hairy.
 * - Default values?
 */

type ZodFieldsFrom<T extends z.ZodObject<ZodRawShape, any>> = T extends z.ZodObject<infer Shape, any> ? Shape : never;

export const BaseField = z.object({
  label: z.string(),
  required: z.boolean(),
  multiValued: z.boolean().optional(),
});
export type BaseField = z.infer<typeof BaseField>;

export const StringField = z.object({
  ...BaseField.shape,
  schemaType: z.literal('string'),
  validValues: z.tuple([z.string()], z.string()).optional(), // TODO labels for values?
});
export type StringField = z.infer<typeof StringField>;

export const EmailField = z.object({
  ...BaseField.shape,
  schemaType: z.literal('email'),
});
export type EmailField = z.infer<typeof EmailField>;

export const PhoneNumberField = z.object({
  ...BaseField.shape,
  schemaType: z.literal('phoneNumber'),
});
export type PhoneNumberField = z.infer<typeof PhoneNumberField>;

// special case of strings where the valid values are known and finite
export const EnumField = z.object({
  ...StringField.shape,
  validValues: z.tuple([z.string()], z.string()), // TODO labels for values?
});
export type EnumField = z.infer<typeof EnumField>;

export const BorrowerField = z.object({
  ...BaseField.shape,
  schemaType: z.literal('borrower'),
});
export type BorrowerField = z.infer<typeof BorrowerField>;

export const NumberField = z.object({
  ...BaseField.shape,
  schemaType: z.literal('number'),
  min: z.number().optional(),
  max: z.number().optional(),
  // NB I'd like to add a refinement here that ensures that min <= max if defined,
  // but that makes NumberField something other than a ZodObject which means we
  // can't extend the form types from it the way we'd like :(
});
export type NumberField = z.infer<typeof NumberField>;

export const CurrencyField = z.object({
  ...BaseField.shape,
  schemaType: z.literal('currency'),
  min: z.number().optional(),
  max: z.number().optional(),
  // NB I'd like to add a refinement here that ensures that min <= max if defined,
  // but that makes NumberField something other than a ZodObject which means we
  // can't extend the form types from it the way we'd like :(
});
export type CurrencyField = z.infer<typeof CurrencyField>;

export const BooleanField = z.object({
  ...BaseField.shape,
  schemaType: z.literal('boolean'),
});
export type BooleanField = z.infer<typeof BooleanField>;

export const PlainDateField = z.object({
  ...BaseField.shape,
  schemaType: z.literal('plainDate'),
  // validation -- minimum and maximum
  // probably should be definable in terms like:
  // - absolute (e.g. 2025-01-01)
  // - relative to the date of rendering (e.g. date must be before today)
  // - relative to interesting loan dates (e.g. date must be after the loan boarding date or before some relevant statement date or something like that?)
});
export type PlainDateField = z.infer<typeof PlainDateField>;

export const BorrowerIdField = z.object({
  ...BaseField.shape,
  schemaType: z.literal('borrowerId'),
});
export type BorrowerIdField = z.infer<typeof BorrowerIdField>;

export const ObjectField: z.ZodObject<
  {
    schemaType: z.ZodLiteral<'object'>;
    fields: z.ZodRecord<z.ZodString, z.ZodLazy<z.ZodTypeAny>>; // recursion breaks these types so we have to use z.ZodTypeAny I guess
  } & ZodFieldsFrom<typeof BaseField>
> = z.object({
  ...BaseField.shape,
  schemaType: z.literal('object'),
  fields: z.record(
    z.string(),
    z.lazy(() => Field),
  ),
}) satisfies z.ZodType<ObjectField>;

export interface ObjectField extends BaseField {
  schemaType: 'object';
  fields: { [key: string]: Field };
}

export const PrimitiveField = z.union([
  StringField,
  EmailField,
  PhoneNumberField,
  NumberField,
  CurrencyField,
  BooleanField,
  PlainDateField,
  EnumField,
  BorrowerField,
]);
export type PrimitiveField = z.infer<typeof PrimitiveField>;

export const ComplexField = ObjectField; // ArrayField? Or do we do multiValued fields as a separate concept?
export type ComplexField = z.infer<typeof ComplexField>;

export const Field = z.union([PrimitiveField, ComplexField]);
export type Field = z.infer<typeof Field>;

export const Schema = z.record(z.string(), Field);
export type Schema = z.infer<typeof Schema>;

export function schemaFromForm(form: Form): Schema {
  return Object.fromEntries(
    form.flatMap(({ key, element }) => fieldFromFormElement(element).map((field) => [key, field])),
  );
}

function fieldFromFormElement(element: FormElement): [Field] | [] {
  if (element.elementType === 'presentationOnlyMarkdown') {
    return [];
  }

  const baseFields: BaseField = {
    label: element.label,
    required: element.required,
    multiValued: element.multiValued,
  };

  switch (element.schemaType) {
    case 'string':
      return [
        {
          ...baseFields,
          schemaType: element.schemaType,
          validValues: element.validValues,
        },
      ];
    case 'number':
    case 'currency':
      return [
        {
          ...baseFields,
          schemaType: element.schemaType,
          min: element.min,
          max: element.max,
        },
      ];
    case 'phoneNumber':
    case 'email':
    case 'plainDate':
    case 'boolean':
    case 'borrower':
      return [
        {
          ...baseFields,
          schemaType: element.schemaType,
        },
      ];
    case 'object':
      return [
        {
          ...baseFields,
          schemaType: 'object',
          fields: Object.fromEntries(
            element.elements.flatMap(({ key, element: childElement }) =>
              fieldFromFormElement(childElement).map((field) => [key, field]),
            ),
          ),
        },
      ];
    default:
      return assertNever(element);
  }
}
